import { Component, computed, inject, signal } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { AvatarComponent } from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { LabelComponent } from '@app/shared/components/label/label.component';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';

export interface MultipleProjectParticipantsDialogData {
  projectParticipants: ProjectParticipant[];
  preselectedProjectParticipants: ProjectParticipant[];
  preselectedLockedProjectParticipantsIds: string[];
  title: string;
}

export interface MultipleProjectParticipantsResultData {
  idsToAdd: string[];
  idsToRemove: string[];
}

@Component({
  selector: 'proffeo-select-multiple-project-participants-dialog',
  templateUrl: './select-multiple-project-participants-dialog.component.html',
  imports: [
    TranslatePipe,
    AvatarComponent,
    LabelComponent,
    UiDialogComponent,
    MatCheckboxModule,
    UiInputComponent,
    UiButtonComponent
  ]
})
export class SelectMultipleProjectParticipantsDialogComponent {
  private readonly data = inject<MultipleProjectParticipantsDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SelectMultipleProjectParticipantsDialogComponent>);
  private readonly lockedIds: ReadonlySet<string> = new Set(this.data.preselectedLockedProjectParticipantsIds ?? []);
  private readonly initialSelectedIds: ReadonlySet<string> = new Set(
    this.data.preselectedProjectParticipants?.map(participant => participant.id) ?? []
  );

  protected readonly AvatarSize = AvatarSize;
  protected readonly title = this.data.title;
  protected readonly projectParticipants: ProjectParticipant[] = this.data.projectParticipants ?? [];
  protected readonly search = signal<ProffeoUiInputValue>('');
  protected readonly selectedIds = signal<ReadonlySet<string>>(
    new Set([...this.initialSelectedIds, ...this.lockedIds])
  );
  protected readonly filteredProjectParticipants = computed((): ProjectParticipant[] => {
    const searchValue = String(this.search() ?? '').toLowerCase();
    if (!searchValue) return this.projectParticipants;

    return this.projectParticipants.filter(participant => participant.user().matchesSearch(searchValue));
  });
  protected readonly allSelected = computed((): boolean => {
    const selected = this.selectedIds();
    return this.projectParticipants.every(participant => selected.has(participant.id));
  });

  protected isSelected(participantId: string): boolean {
    return this.selectedIds().has(participantId);
  }

  protected isLockedById(participantId: string): boolean {
    return this.lockedIds.has(participantId);
  }

  protected onSelectAllChange(event: MatCheckboxChange): void {
    const next = new Set(this.selectedIds());
    for (const participant of this.projectParticipants) {
      if (this.isLockedById(participant.id)) continue;
      if (event.checked) {
        next.add(participant.id);
      } else {
        next.delete(participant.id);
      }
    }
    this.selectedIds.set(next);
  }

  protected onParticipantChange(event: MatCheckboxChange, participantId: string): void {
    const next = new Set(this.selectedIds());
    if (event.checked) {
      next.add(participantId);
    } else {
      next.delete(participantId);
    }
    this.selectedIds.set(next);
  }

  protected handleSaveClick(): void {
    const selected = this.selectedIds();
    const currentSelectedIds = new Set(
      this.projectParticipants
        .filter(participant => selected.has(participant.id) && !this.isLockedById(participant.id))
        .map(participant => participant.id)
    );

    const idsToAdd = Array.from(currentSelectedIds).filter(id => !this.initialSelectedIds.has(id));
    const idsToRemove = Array.from(this.initialSelectedIds).filter(
      id => !currentSelectedIds.has(id) && !this.isLockedById(id)
    );

    const result: MultipleProjectParticipantsResultData = {
      idsToAdd: idsToAdd,
      idsToRemove: idsToRemove
    };

    this.dialogRef.close(result);
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
